import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';

const lowlight = createLowlight(common);

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Escribe aquí...',
  minHeight = '150px',
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      CodeBlockLowlight.configure({ lowlight }),
      Underline,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Placeholder.configure({ placeholder }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse table-auto w-full',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || '<p></p>',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none focus:outline-none p-3 min-h-[${minHeight}]`,
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '<p></p>');
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL del enlace', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  return (
    <div className="border rounded">
      <div className="flex flex-wrap gap-1 p-2 bg-gray-100 border-b text-sm" role="toolbar" aria-label="Editor de formato">
        {/* Negrita, cursiva, subrayado */}
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-1.5 rounded ${editor.isActive('bold') ? 'bg-blue-600 text-white' : 'bg-white'}`}
          title="Negrita"
          aria-label="Negrita"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-1.5 rounded ${editor.isActive('italic') ? 'bg-blue-600 text-white' : 'bg-white'}`}
          title="Cursiva"
          aria-label="Cursiva"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-1.5 rounded ${editor.isActive('underline') ? 'bg-blue-600 text-white' : 'bg-white'}`}
          title="Subrayado"
          aria-label="Subrayado"
        >
          <u>U</u>
        </button>

        {/* Encabezados */}
        <div className="border-l pl-2 ml-1 flex gap-1">
          {[1, 2, 3].map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 }).run()}
              className={`px-2 py-1 rounded text-xs ${editor.isActive('heading', { level }) ? 'bg-blue-600 text-white' : 'bg-white'
                }`}
              title={`Encabezado H${level}`}
              aria-label={`Encabezado nivel ${level}`}
            >
              H{level}
            </button>
          ))}
        </div>

        {/* Listas */}
        <div className="border-l pl-2 ml-1 flex gap-1">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`p-1.5 rounded ${editor.isActive('bulletList') ? 'bg-blue-600 text-white' : 'bg-white'}`}
            title="Lista con viñetas"
            aria-label="Lista con viñetas"
          >
            •
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`p-1.5 rounded ${editor.isActive('orderedList') ? 'bg-blue-600 text-white' : 'bg-white'}`}
            title="Lista numerada"
            aria-label="Lista numerada"
          >
            1.
          </button>
        </div>

        {/* Alineación */}
        <div className="border-l pl-2 ml-1 flex gap-1">
          {(['left', 'center', 'right', 'justify'] as const).map((align) => (
            <button
              key={align}
              type="button"
              onClick={() => editor.chain().focus().setTextAlign(align).run()}
              className={`p-1.5 rounded ${editor.isActive({ textAlign: align }) ? 'bg-blue-600 text-white' : 'bg-white'
                }`}
              title={`Alinear ${align}`}
              aria-label={`Alinear ${align}`}
            >
              {align === 'left' && '〈'}
              {align === 'center' && '≡'}
              {align === 'right' && '〉'}
              {align === 'justify' && '☰'}
            </button>
          ))}
        </div>

        {/* Enlace */}
        <div className="border-l pl-2 ml-1">
          <button
            type="button"
            onClick={setLink}
            className={`p-1.5 rounded ${editor.isActive('link') ? 'bg-blue-600 text-white' : 'bg-white'}`}
            title="Enlace"
            aria-label="Insertar o editar enlace"
          >
            🔗
          </button>
        </div>

        {/* Bloque de código */}
        <div className="border-l pl-2 ml-1">
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={`p-1.5 rounded ${editor.isActive('codeBlock') ? 'bg-blue-600 text-white' : 'bg-white'}`}
            title="Bloque de código"
            aria-label="Bloque de código"
          >
            {"</>"}
          </button>
        </div>

        {/* Línea horizontal */}
        <div className="border-l pl-2 ml-1">
          <button
            type="button"
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            className="p-1.5 rounded bg-white"
            title="Línea horizontal"
            aria-label="Insertar línea horizontal"
          >
            ―
          </button>
        </div>

        {/* Tabla */}
        <div className="border-l pl-2 ml-1 flex gap-1">
          <button
            type="button"
            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            className={`px-2 py-1 rounded text-xs ${editor.isActive('table') ? 'bg-blue-600 text-white' : 'bg-white'}`}
            title="Insertar tabla 3x3"
            aria-label="Insertar tabla"
          >
            📊
          </button>
          {editor.isActive('table') && (
            <>
              <button
                type="button"
                onClick={() => editor.chain().focus().addColumnBefore().run()}
                className="px-2 py-1 rounded text-xs bg-white"
                title="Agregar columna antes"
                aria-label="Agregar columna antes"
              >
                ⬅️+
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                className="px-2 py-1 rounded text-xs bg-white"
                title="Agregar columna después"
                aria-label="Agregar columna después"
              >
                +➡️
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().deleteColumn().run()}
                className="px-2 py-1 rounded text-xs bg-white"
                title="Eliminar columna"
                aria-label="Eliminar columna"
              >
                ❌↔️
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().addRowBefore().run()}
                className="px-2 py-1 rounded text-xs bg-white"
                title="Agregar fila antes"
                aria-label="Agregar fila antes"
              >
                ⬆️+
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().addRowAfter().run()}
                className="px-2 py-1 rounded text-xs bg-white"
                title="Agregar fila después"
                aria-label="Agregar fila después"
              >
                +⬇️
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().deleteRow().run()}
                className="px-2 py-1 rounded text-xs bg-white"
                title="Eliminar fila"
                aria-label="Eliminar fila"
              >
                ❌↕️
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().deleteTable().run()}
                className="px-2 py-1 rounded text-xs bg-red-500 text-white"
                title="Eliminar tabla"
                aria-label="Eliminar tabla"
              >
                🗑️
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().mergeCells().run()}
                className="px-2 py-1 rounded text-xs bg-white"
                title="Combinar celdas"
                aria-label="Combinar celdas"
              >
                ⬛
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().splitCell().run()}
                className="px-2 py-1 rounded text-xs bg-white"
                title="Dividir celda"
                aria-label="Dividir celda"
              >
                ⬜⬜
              </button>
              <button
                type="button"
                onClick={() => editor.chain().focus().toggleHeaderRow().run()}
                className="px-2 py-1 rounded text-xs bg-white"
                title="Alternar fila de encabezado"
                aria-label="Alternar fila de encabezado"
              >
                📌
              </button>
            </>
          )}
        </div>
      </div>

      <EditorContent editor={editor} />
    </div>
  );
};

export default React.memo(RichTextEditor);
